/**
 * Password Policy (frontend mirror)
 *
 * MUST stay in sync with stacked-backend/common/passwordPolicy.js. The
 * backend is the source of truth — this file exists so the signup form
 * can show real-time strength feedback without round-tripping every
 * keystroke. If they ever drift, the backend will reject and the user
 * will see the discrepancy.
 *
 * If you change the rules: update both files, ideally in the same PR.
 */

const COMMON_PASSWORDS = new Set([
  "password", "password1", "password!", "password1!", "password123",
  "password123!", "p@ssword", "p@ssword1", "p@ssword!", "p@ssword1!",
  "passw0rd", "passw0rd!", "passw0rd1", "p@ssw0rd", "p@ssw0rd!",
  "12345678", "123456789", "1234567890", "qwerty", "qwerty1", "qwerty!",
  "qwerty123", "qwerty123!", "qwertyuiop",
  "abc12345", "abcd1234", "letmein", "letmein1", "letmein!", "letmein123",
  "welcome", "welcome1", "welcome!", "welcome1!", "welcome123", "welcome123!",
  "admin", "admin1", "admin!", "admin123", "admin123!", "administrator",
  "iloveyou", "iloveyou1", "iloveyou!", "1q2w3e4r",
  "trustno1", "sunshine", "sunshine1", "princess", "princess1",
  "football", "baseball", "monkey", "monkey1",
  "dragon", "dragon1", "master", "master1", "superman", "batman",
  "hello123", "hello123!", "hellothere", "hellothere!",
  "changeme", "changeme1", "changeme!", "changeme1!",
  "default", "default1", "default!", "default1!",
  "qazwsx", "1qaz2wsx",
  "stacked", "stacked1", "stacked!", "stacked1!", "stacked123",
  "stackedcrm", "getstacked",
  "realestate", "realestate1", "realestate!", "realestate1!",
  "wholesale", "wholesale1", "probate", "probate1", "flipper", "flipper1",
]);

export const PASSWORD_POLICY = {
  MIN_LENGTH: 10,
  MAX_LENGTH: 128,
};

function hasSequentialRun(str, len) {
  if (str.length < len) return false;
  let asc = 1;
  let desc = 1;
  for (let i = 1; i < str.length; i++) {
    const diff = str.charCodeAt(i) - str.charCodeAt(i - 1);
    if (diff === 1) {
      asc++; desc = 1;
      if (asc >= len) return true;
    } else if (diff === -1) {
      desc++; asc = 1;
      if (desc >= len) return true;
    } else {
      asc = 1; desc = 1;
    }
  }
  return false;
}

/**
 * Return per-rule pass/fail for live strength feedback.
 *
 * @param {string} password
 * @param {object} [context] - { email?, name? }
 * @returns {Array<{id, label, passed}>}
 */
export function evaluatePasswordRules(password, context = {}) {
  const pwd = typeof password === "string" ? password : "";
  const hasUpper = /[A-Z]/.test(pwd);
  const hasLower = /[a-z]/.test(pwd);
  const hasDigit = /[0-9]/.test(pwd);
  const hasSpecial = /[^A-Za-z0-9]/.test(pwd);
  const classCount = [hasUpper, hasLower, hasDigit, hasSpecial].filter(Boolean).length;

  const lowerPwd = pwd.toLowerCase();
  const local = context.email ? (context.email.split("@")[0] || "").toLowerCase() : "";
  const localTokens = local.split(/[-_.+]/).filter((t) => t.length >= 4);
  const containsEmail =
    (local.length >= 4 && lowerPwd.includes(local)) ||
    localTokens.some((t) => lowerPwd.includes(t));

  const nameTokens = context.name
    ? context.name.toLowerCase().split(/\s+/).filter((t) => t.length >= 4)
    : [];
  const containsName = nameTokens.some((t) => lowerPwd.includes(t));

  return [
    { id: "length", label: `At least ${PASSWORD_POLICY.MIN_LENGTH} characters`, passed: pwd.length >= PASSWORD_POLICY.MIN_LENGTH },
    { id: "complexity", label: "3 of: uppercase, lowercase, number, special character", passed: classCount >= 3 },
    { id: "notCommon", label: "Not a commonly-used password", passed: !COMMON_PASSWORDS.has(pwd.toLowerCase()) && pwd.length > 0 },
    { id: "noRuns", label: "No long runs of repeated or sequential characters", passed: pwd.length > 0 && !/(.)\1{3,}/.test(pwd) && !hasSequentialRun(pwd, 5) },
    { id: "notIdentity", label: "Doesn't contain your email or name", passed: !containsEmail && !containsName },
  ];
}

/**
 * Returns true if the password satisfies every rule. Use this to gate the
 * submit button so users can't even attempt to submit a noncompliant pwd.
 */
export function passwordMeetsPolicy(password, context = {}) {
  if (!password || password.length > PASSWORD_POLICY.MAX_LENGTH) return false;
  return evaluatePasswordRules(password, context).every((r) => r.passed);
}
